import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Loader2, Search, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { currentLocaleTag } from '../lib/formatting'

// Gemela de FINANCIAL_ENTITIES en apps/api/src/routes/audit-financial.ts.
type FinancialEntity =
  | 'invoice'
  | 'expense'
  | 'journal_entry'
  | 'account'
  | 'payment_milestone'
  | 'bank_transaction'

interface AuditUser {
  id: string
  name: string | null
  email: string | null
}

interface AuditRow {
  id: string
  action: string
  entity: FinancialEntity
  entityId: string | null
  ip: string | null
  userAgent: string | null
  metadata: unknown
  createdAt: string
  user: AuditUser | null
}

interface AuditResponse {
  items: AuditRow[]
  total: number
  limit: number
}

const ENTITY_PILL: Record<FinancialEntity, string> = {
  invoice: 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/40',
  expense: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  journal_entry: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  account: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/40',
  payment_milestone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  bank_transaction: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function monthsAgoISO(n: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return d.toISOString().slice(0, 10)
}

export default function AccountingAudit() {
  const { t } = useTranslation('accounting')
  const [entity, setEntity] = useState<'' | FinancialEntity>('')
  const [from, setFrom] = useState<string>(monthsAgoISO(3))
  const [to, setTo] = useState<string>(todayISO())
  const [q, setQ] = useState<string>('')
  const [rows, setRows] = useState<AuditRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ENTITY_OPTIONS: Array<{ value: '' | FinancialEntity; label: string }> = [
    { value: '', label: t('audit.entity.all') },
    { value: 'invoice', label: t('audit.entity.invoice') },
    { value: 'expense', label: t('audit.entity.expense') },
    { value: 'journal_entry', label: t('audit.entity.journalEntry') },
    { value: 'account', label: t('audit.entity.account') },
    { value: 'payment_milestone', label: t('audit.entity.paymentMilestone') },
    { value: 'bank_transaction', label: t('audit.entity.bankTransaction') },
  ]

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (entity) params.set('entity', entity)
      if (from) params.set('from', `${from}T00:00:00.000Z`)
      if (to) params.set('to', `${to}T23:59:59.999Z`)
      if (q.trim()) params.set('q', q.trim())
      const data = await api.get<AuditResponse>(`/accounting/audit?${params.toString()}`)
      setRows(data.items)
      setTotal(data.total)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('audit.errors.loadFailed')
      setError(text)
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [entity, from, to, q, t])

  useEffect(() => {
    void reload()
  }, [reload])

  const formattedRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        when: new Date(r.createdAt).toLocaleString(currentLocaleTag()),
        who: r.user?.name ?? r.user?.email ?? '—',
        metadataPretty:
          r.metadata != null
            ? typeof r.metadata === 'string'
              ? r.metadata
              : JSON.stringify(r.metadata)
            : '',
      })),
    [rows],
  )

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <ShieldCheck size={20} className="text-toga-700 dark:text-toga-300" />
            {t('audit.title')}
          </h1>
          <p className="text-xs t-muted">
            {t('audit.subtitle')}
          </p>
        </div>
      </header>

      <section
        className="rounded-xl border p-4 grid grid-cols-1 md:grid-cols-5 gap-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('audit.filters.entity')}
          <select
            value={entity}
            onChange={(e) => setEntity(e.target.value as '' | FinancialEntity)}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
          >
            {ENTITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('audit.filters.from')}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('audit.filters.to')}
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs t-muted md:col-span-2">
          {t('audit.filters.search')}
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 t-muted pointer-events-none"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('audit.filters.searchPlaceholder')}
              className="w-full rounded-md border pl-7 pr-2 py-1.5 text-sm"
              style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
            />
          </div>
        </label>
      </section>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <section
        className="rounded-xl border"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <div
          className="flex items-center justify-between px-4 py-2 text-xs t-muted"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <span>
            {loading ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" />
                {t('audit.loading')}
              </span>
            ) : (
              t('audit.recordsCount', { count: total })
            )}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs t-muted uppercase tracking-wider"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <th className="px-4 py-2">{t('audit.columns.when')}</th>
                <th className="px-4 py-2">{t('audit.columns.entity')}</th>
                <th className="px-4 py-2">{t('audit.columns.action')}</th>
                <th className="px-4 py-2">{t('audit.columns.entityId')}</th>
                <th className="px-4 py-2">{t('audit.columns.user')}</th>
                <th className="px-4 py-2">{t('audit.columns.ip')}</th>
                <th className="px-4 py-2">{t('audit.columns.metadata')}</th>
              </tr>
            </thead>
            <tbody>
              {formattedRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-xs t-muted">
                    {t('audit.empty')}
                  </td>
                </tr>
              ) : (
                formattedRows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ borderBottom: '1px solid var(--border)' }}
                    className="t-primary"
                  >
                    <td className="px-4 py-1.5 whitespace-nowrap text-xs">{r.when}</td>
                    <td className="px-4 py-1.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${ENTITY_PILL[r.entity]}`}
                      >
                        {r.entity}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 font-mono text-xs">{r.action}</td>
                    <td className="px-4 py-1.5 font-mono text-xs t-muted">{r.entityId ?? '—'}</td>
                    <td className="px-4 py-1.5 text-xs">{r.who}</td>
                    <td className="px-4 py-1.5 font-mono text-xs t-muted">{r.ip ?? '—'}</td>
                    <td className="px-4 py-1.5 font-mono text-[11px] t-muted max-w-md truncate">
                      {r.metadataPretty}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
